import { ROUTES, SITE_URL } from "@/i18n";
import { SCHOLAR_PROFILES } from "@/data/scholarProfiles";
import pl from "@/i18n/pl.json";
import en from "@/i18n/en.json";

const DICTS = { pl, en };

const PERSON_ID = `${SITE_URL}/#person`;

const TEXT = {
  en: {
    jobTitle: "Medical Oncologist",
    description: "Medical oncologist focused on individualized systemic treatment strategies for advanced, treatment-refractory cancers, including multidrug metronomic chemotherapy.",
    uj: "Jagiellonian University Medical College",
    ujDept: "Department of Oncology, Jagiellonian University Medical College",
    hospital: "University Hospital in Kraków",
    ptok: "Polish Society of Clinical Oncology",
    president: "President",
    alumni: "Poznan University of Medical Sciences",
    knowsAbout: ["Medical oncology", "Breast cancer", "Genitourinary cancers", "Metronomic chemotherapy", "Immunotherapy", "Systemic cancer therapy", "Second opinion in oncology"],
  },
  pl: {
    jobTitle: "Specjalista onkologii klinicznej",
    description: "Specjalista onkologii klinicznej zajmujący się indywidualnymi strategiami leczenia systemowego zaawansowanych nowotworów, w tym wielolekową chemioterapią metronomiczną.",
    uj: "Uniwersytet Jagielloński – Collegium Medicum",
    ujDept: "Katedra i Klinika Onkologii, Uniwersytet Jagielloński – Collegium Medicum",
    hospital: "Szpital Uniwersytecki w Krakowie",
    ptok: "Polskie Towarzystwo Onkologii Klinicznej",
    president: "Prezes",
    alumni: "Akademia Medyczna im. Karola Marcinkowskiego w Poznaniu",
    knowsAbout: ["Onkologia kliniczna", "Rak piersi", "Nowotwory układu moczowo-płciowego", "Chemioterapia metronomiczna", "Immunoterapia", "Leczenie systemowe nowotworów", "Druga opinia onkologiczna"],
  },
};

const ORCID = "https://orcid.org/0000-0003-4003-5278";
const UJ_URL = "https://www.cm-uj.krakow.pl";
const HOSPITAL_URL = "https://www.su.krakow.pl";
const WORK_ADDRESS = { "@type": "PostalAddress", streetAddress: "ul. Kopernika 50", postalCode: "31-501", addressLocality: "Kraków", addressCountry: "PL" };

export const personSchema = (lang) => {
  const t = TEXT[lang];
  const hospital = { "@type": "Hospital", name: t.hospital, url: HOSPITAL_URL, address: WORK_ADDRESS };
  return {
    "@type": ["Person", "IndividualPhysician"],
    "@id": PERSON_ID,
    name: "Piotr Wysocki",
    givenName: "Piotr",
    familyName: "Wysocki",
    honorificPrefix: lang === "en" ? "Prof." : "Prof. dr hab. n. med.",
    honorificSuffix: lang === "en" ? "MD, PhD" : undefined,
    jobTitle: t.jobTitle,
    description: t.description,
    url: `${SITE_URL}${ROUTES.about[lang]}`,
    image: `${SITE_URL}/images/portrait.jpg`,
    hasOccupation: { "@type": "Occupation", name: t.jobTitle },
    medicalSpecialty: { "@type": "MedicalSpecialty", "@id": "https://schema.org/Oncologic", name: "Medical Oncology" },
    address: WORK_ADDRESS,
    identifier: { "@type": "PropertyValue", propertyID: "ORCID", value: "0000-0003-4003-5278", url: ORCID },
    worksFor: [
      { "@type": "CollegeOrUniversity", name: t.uj, url: UJ_URL, department: { "@type": "Organization", name: t.ujDept } },
      hospital,
    ],
    affiliation: { "@type": "CollegeOrUniversity", name: t.uj, url: UJ_URL },
    hospitalAffiliation: hospital,
    memberOf: [
      {
        "@type": "OrganizationRole",
        roleName: t.president,
        startDate: "2015",
        endDate: "2023",
        memberOf: { "@type": "Organization", name: t.ptok, url: "https://www.ptok.pl" },
      },
      { "@type": "OrganizationRole", startDate: "2008", memberOf: { "@type": "Organization", name: "American Society of Clinical Oncology (ASCO)", url: "https://www.asco.org" } },
      { "@type": "OrganizationRole", startDate: "2010", memberOf: { "@type": "Organization", name: "American Association for Cancer Research (AACR)", url: "https://www.aacr.org" } },
    ],
    alumniOf: { "@type": "CollegeOrUniversity", name: t.alumni },
    award: DICTS[lang].about.awards.map(([year, text]) => `${text.replace(/\.$/, "")} (${year})`),
    knowsAbout: t.knowsAbout,
    sameAs: [...new Set([ORCID, ...SCHOLAR_PROFILES.filter((p) => p.id !== "pubmed").map((p) => p.href)])],
  };
};

const graph = (...nodes) => ({ "@context": "https://schema.org", "@graph": nodes });

const pageNode = (lang, page, type = "WebPage") => {
  const url = `${SITE_URL}${ROUTES[page][lang]}`;
  const { title, description } = DICTS[lang].seo[page];
  return {
    "@type": type,
    "@id": `${url}#webpage`,
    url,
    name: title,
    description,
    inLanguage: lang === "en" ? "en-US" : "pl-PL",
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": PERSON_ID },
  };
};

export const webPageSchema = (lang, page) => graph(pageNode(lang, page));

export const homeSchema = (lang) =>
  graph(
    { "@type": "WebSite", "@id": `${SITE_URL}/#website`, url: `${SITE_URL}/`, name: "ONKOLIFE", inLanguage: ["en-US", "pl-PL"], publisher: { "@id": PERSON_ID } },
    pageNode(lang, "home"),
    personSchema(lang)
  );

export const profilePageSchema = (lang) =>
  graph({ ...pageNode(lang, "about", "ProfilePage"), mainEntity: personSchema(lang) });

export const publicationsSchema = (lang, items) =>
  graph({
    ...pageNode(lang, "publications", "CollectionPage"),
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: items.length,
      itemListElement: items.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": p.type === "chapter" ? "Chapter" : p.type === "book" ? "Book" : "ScholarlyArticle",
          name: p.title || p.citation,
          citation: p.citation,
          datePublished: String(p.year),
          url: p.url || undefined,
          sameAs: [p.doi && `https://doi.org/${p.doi}`, p.pmid && `https://pubmed.ncbi.nlm.nih.gov/${p.pmid}/`].filter(Boolean),
        },
      })),
    },
  });
